import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Autocomplete,
    CircularProgress,
    Box,
} from '@mui/material';
import { useAllUsersQuery } from '../../api/JsdhNotifikaceService/service/AdminMutations';
import type { CreateIotPrijemceRequest, Uzivatel } from '../../api/JsdhNotifikaceService/generated';

interface PrijemceFormDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: CreateIotPrijemceRequest) => void;
    isLoading?: boolean;
    title?: string;
}

const PrijemceFormDialog: React.FC<PrijemceFormDialogProps> = ({
    open,
    onClose,
    onSubmit,
    isLoading = false,
    title = 'Přidat příjemce',
}) => {
    const [mode, setMode] = useState<'user' | 'email'>('user');
    const [selectedUser, setSelectedUser] = useState<Uzivatel | null>(null);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');

    const { data: users, isLoading: usersLoading } = useAllUsersQuery();

    const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setMode(event.target.value as 'user' | 'email');
        setSelectedUser(null);
        setEmail('');
        setEmailError('');
    };

    const validateEmail = (value: string): boolean => {
        if (!value) {
            setEmailError('Email je povinný');
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            setEmailError('Neplatný formát emailu');
            return false;
        }
        setEmailError('');
        return true;
    };

    const handleSubmit = () => {
        if (mode === 'user' && selectedUser) {
            onSubmit({ uzivatelId: selectedUser.id });
        } else if (mode === 'email' && validateEmail(email)) {
            onSubmit({ email });
        }
    };

    const handleClose = () => {
        setMode('user');
        setSelectedUser(null);
        setEmail('');
        setEmailError('');
        onClose();
    };

    const isSubmitDisabled = () => {
        if (isLoading) return true;
        if (mode === 'user') return !selectedUser;
        if (mode === 'email') return !email || !!emailError;
        return true;
    };

    // Filter out users without email (needed for FCM notifications)
    const activeUsers = users?.filter((u) => u.aktivni) || [];

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <FormControl component="fieldset" sx={{ mb: 2 }}>
                        <RadioGroup row value={mode} onChange={handleModeChange}>
                            <FormControlLabel
                                value="user"
                                control={<Radio />}
                                label="Vybrat uživatele"
                            />
                            <FormControlLabel
                                value="email"
                                control={<Radio />}
                                label="Zadat email"
                            />
                        </RadioGroup>
                    </FormControl>

                    {mode === 'user' && (
                        <Autocomplete
                            options={activeUsers}
                            getOptionLabel={(option) =>
                                `${option.jmeno}${option.email ? ` (${option.email})` : ''}`
                            }
                            value={selectedUser}
                            onChange={(_, newValue) => setSelectedUser(newValue)}
                            loading={usersLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Uživatel"
                                    placeholder="Vyhledat uživatele..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {usersLoading && (
                                                    <CircularProgress color="inherit" size={20} />
                                                )}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText="Žádný uživatel nenalezen"
                            fullWidth
                        />
                    )}

                    {mode === 'email' && (
                        <TextField
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (emailError) validateEmail(e.target.value);
                            }}
                            onBlur={() => validateEmail(email)}
                            error={!!emailError}
                            helperText={emailError || 'Email pro zasílání notifikací'}
                            fullWidth
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>
                    Zrušit
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isSubmitDisabled()}
                >
                    {isLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
                    Přidat
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PrijemceFormDialog;
